import type { Variety } from "./types";

// PASTE #394: client PRICING TIER layer (FOUNDATION). Adds a per-client
// tier on TOP of the existing resolution chain:
//
//   client explicit override  ->  client TIER price  ->  global catalog price
//
// This module is PURE (no server/node imports) so it is safe in the client
// bundle - it sits under lib/catalog/pricing.ts (catalogPricePerStem) and
// lib/catalog/client-pricing.ts (applyClientPricing), the SAME two entry
// points that back both the client display and the server re-derive / QB
// invoice. Routing every surface through this one function is what keeps
// "client display == server re-derive == invoice line" (CRIT-1) intact for
// tier clients too.
//
// CRITICAL INVARIANT (PASTE #394): a tier of `undefined` (every client that
// exists today) is a strict no-op - tierPricePerStem returns null so the
// caller falls through to the exact global/override behavior it has now. No
// current client carries a tier (the field is only ever written by the #395
// admin selector), so every existing client's resolved price is byte-
// identical before and after this change. Proven by tier-pricing.test.ts.
//
// TIER SEMANTICS (source of truth: MAGNAFLOR_TIER_PRICES.csv, 2026-08-14):
//   - flower_shop = the existing global catalog price (unchanged). No-op.
//   - event       = global x 1.10, rounded to cents (a RULE, not a table).
//   - wholesaler  = the explicit wholesale table below (a real per-SKU
//                   discount that is NOT a fixed percentage, so it must be
//                   a lookup: e.g. rose 0.63/0.79 vs garden 0.84/1.29).
// Every tier price is clamped to the variety's landed cost floor and an
// owner alert is logged if the clamp fires (foundation: a tagged warn; ops
// can route it to SES later).

export type PricingTier = "flower_shop" | "wholesaler" | "event";

export const PRICING_TIERS: readonly PricingTier[] = [
  "flower_shop",
  "wholesaler",
  "event",
];

export function isPricingTier(v: unknown): v is PricingTier {
  return v === "flower_shop" || v === "wholesaler" || v === "event";
}

// Human labels for the admin tier selector (#395). flower_shop is the
// default (global price = no change).
export const TIER_LABELS: Record<PricingTier, string> = {
  flower_shop: "Flower shop",
  wholesaler: "Wholesaler",
  event: "Event",
};

// event = flower_shop (global) x 1.10.
export const EVENT_MULTIPLIER = 1.1;

// Cents rounding, identical to lib/catalog/pricing.ts roundUsd. Kept local
// so this module never imports pricing.ts (pricing.ts imports THIS - the
// dependency points one way only).
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// Minimal shape needed to classify + key a variety.
type TierClassifiable = Pick<Variety, "slug" | "name" | "category">;

type WholesaleEntry = { wholesale: number; floor: number };

// Standard rose (category `rose`, NON-tinted) - keyed by length like the
// global rose list. Blue Tinted rose is a distinct SKU (SLUG_WHOLESALE).
const ROSE_WHOLESALE: Record<number, WholesaleEntry> = {
  50: { wholesale: 0.63, floor: 0.4704 },
  60: { wholesale: 0.7, floor: 0.5238 },
  70: { wholesale: 0.77, floor: 0.5772 },
};

// Garden rose (category `garden-spray-rose`, NON-spray) - flat 50-60.
const GARDEN_ROSE_WHOLESALE: WholesaleEntry = { wholesale: 0.84, floor: 0.6306 };

// Spray rose (category `garden-spray-rose`, spray) - flat 50-60.
const SPRAY_ROSE_WHOLESALE: WholesaleEntry = { wholesale: 0.7, floor: 0.5238 };

// Per-SKU flat wholesale (blue-tinted rose + every hydrangea / specialty /
// filler). Keyed by the live catalog slug (verified against the live DDB
// catalog, 117 SKUs, on 2026-08-14). PASTE #395: every sellable hydrangea /
// specialty / filler SKU is now mapped, so a wholesaler client never silently
// falls through to retail (the coverage guard in tier-pricing.test.ts fails
// the build if any live SKU is left unmapped). Reconciled data decisions:
//   - Bells of Ireland, Hydrangea Green Select, Bupleurum, Leather Fern,
//     Lysimachia, Scabiosa Stellata: wholesale rows added to the CSV by
//     Alexis 2026-08-14 (previously fell through to retail).
//   - "Hydrangea Dark Blue Premium": NOT a live product - Alexis confirmed
//     it is not sold; the orphan CSV row was removed. Not listed here.
// A slug still absent is fail-safe (wholesaleEntry -> null -> global price,
// never a mis-bill), but the coverage guard now asserts there are none.
const SLUG_WHOLESALE: Record<string, WholesaleEntry> = {
  // Blue Tinted rose (excluded from the standard-rose bucket).
  "rose-blue-tinted-60": { wholesale: 1.2, floor: 0.8976 },
  // Hydrangeas (sold by box; one wholesale price per grade SKU).
  "hydrangea-white-select": { wholesale: 1.07, floor: 0.8563 },
  "hydrangea-blue-select": { wholesale: 1.14, floor: 0.9125 },
  "hydrangea-green-mini": { wholesale: 0.86, floor: 0.6875 },
  "hydrangea-green-select": { wholesale: 1.07, floor: 0.8563 },
  "hydrangea-dark-green-select": { wholesale: 1.38, floor: 1.1038 },
  "hydrangea-dark-blue-select": { wholesale: 1.13, floor: 0.9012 },
  "hydrangea-pink-select": { wholesale: 1.14, floor: 0.9125 },
  "hydrangea-purple-premium": { wholesale: 1.35, floor: 1.0813 },
  "hydrangea-white-premium": { wholesale: 1.21, floor: 0.9688 },
  "hydrangea-blue-premium": { wholesale: 1.35, floor: 1.0813 },
  // Specialty.
  "asiatic-lily": { wholesale: 1.01, floor: 0.8042 },
  "bells-of-ireland": { wholesale: 0.51, floor: 0.409 },
  delphinium: { wholesale: 0.67, floor: 0.5372 },
  larkspur: { wholesale: 0.67, floor: 0.5372 },
  lisianthus: { wholesale: 0.67, floor: 0.5372 },
  lysimachia: { wholesale: 0.64, floor: 0.5158 },
  "oriental-lily": { wholesale: 1.47, floor: 1.178 },
  ranunculus: { wholesale: 0.67, floor: 0.5372 },
  "royal-chrysanthemum": { wholesale: 0.6, floor: 0.4838 },
  "scabiosa-stellata": { wholesale: 0.6, floor: 0.4838 },
  "select-carnation": { wholesale: 0.35, floor: 0.2809 },
  snapdragon: { wholesale: 0.56, floor: 0.4518 },
  "spray-carnation": { wholesale: 0.4, floor: 0.3236 },
  stock: { wholesale: 0.51, floor: 0.409 },
  sunflower: { wholesale: 0.6, floor: 0.4838 },
  veronica: { wholesale: 0.54, floor: 0.4304 },
  // Fillers & greens.
  "achillea-yellow": { wholesale: 0.54, floor: 0.4304 },
  alstroemeria: { wholesale: 0.56, floor: 0.4518 },
  "babys-breath": { wholesale: 0.56, floor: 0.4518 },
  brillantina: { wholesale: 0.54, floor: 0.4304 },
  craspedia: { wholesale: 0.51, floor: 0.409 },
  "dusty-miller": { wholesale: 0.6, floor: 0.4838 },
  bupleurum: { wholesale: 0.64, floor: 0.5158 },
  "eucalyptus-baby-blue": { wholesale: 0.47, floor: 0.377 },
  "eucalyptus-silver-dollar": { wholesale: 0.56, floor: 0.4518 },
  "eucalyptus-parvifolia": { wholesale: 0.54, floor: 0.4304 },
  "green-ball": { wholesale: 0.54, floor: 0.4304 },
  hypericum: { wholesale: 0.56, floor: 0.4518 },
  "leather-fern": { wholesale: 0.35, floor: 0.2809 },
  lepidium: { wholesale: 0.64, floor: 0.5158 },
  "lily-grass": { wholesale: 0.24, floor: 0.1954 },
  limonium: { wholesale: 0.6, floor: 0.4838 },
  "rumex-green": { wholesale: 0.35, floor: 0.2809 },
  ruscus: { wholesale: 0.47, floor: 0.377 },
  scabiosa: { wholesale: 0.87, floor: 0.6974 },
  smilax: { wholesale: 0.43, floor: 0.345 },
  solidago: { wholesale: 0.48, floor: 0.3877 },
  statice: { wholesale: 0.48, floor: 0.3877 },
  strawflower: { wholesale: 0.54, floor: 0.4304 },
};

function isSprayRose(variety: TierClassifiable): boolean {
  return `${variety.slug} ${variety.name}`.toLowerCase().includes("spray");
}

// The wholesale + floor entry for a variety+length, or null when the SKU is
// not on any tier table (fail-safe: caller falls through to global).
function wholesaleEntry(
  variety: TierClassifiable,
  lengthCm: number | null | undefined,
): WholesaleEntry | null {
  const bySlug = SLUG_WHOLESALE[variety.slug];
  if (bySlug) return bySlug;
  if (variety.category === "rose") {
    // Standard rose, length-keyed. Tinted is caught by SLUG_WHOLESALE above.
    if (lengthCm != null && ROSE_WHOLESALE[lengthCm]) {
      return ROSE_WHOLESALE[lengthCm];
    }
    return null;
  }
  if (variety.category === "garden-spray-rose") {
    return isSprayRose(variety) ? SPRAY_ROSE_WHOLESALE : GARDEN_ROSE_WHOLESALE;
  }
  return null;
}

// Owner alert when a tier price would resolve below the landed cost floor.
// Foundation-level: a tagged warn (scannable). Ops can route [OWNER-ALERT]
// to SES later - kept side-effect-only so it never changes the return path.
export function logTierFloorClamp(
  slug: string,
  lengthCm: number | null | undefined,
  tier: PricingTier,
  raw: number,
  floor: number,
): void {
  console.warn(
    `[OWNER-ALERT][tier-floor-clamp] ${tier} price for ${slug}` +
      `${lengthCm != null ? ` @${lengthCm}cm` : ""} would be ${raw.toFixed(4)}` +
      ` below landed cost floor ${floor.toFixed(4)} - clamped to floor.`,
  );
}

// Clamp a raw tier price to the variety's landed cost floor. Logs an owner
// alert if the clamp fires. When no floor is known for the SKU (not on any
// tier table), the raw price passes through unclamped.
function clampToFloor(
  variety: TierClassifiable,
  lengthCm: number | null | undefined,
  tier: PricingTier,
  raw: number,
): number {
  const entry = wholesaleEntry(variety, lengthCm);
  const floor = entry?.floor;
  if (floor != null && raw < floor) {
    logTierFloorClamp(variety.slug, lengthCm, tier, raw, floor);
    return round2(floor);
  }
  return raw;
}

// The per-stem TIER price for a variety+length, or null when no tier applies
// (so the caller falls through to the global catalog price). Fail-safe by
// construction: an undefined/flower_shop tier, or a wholesaler SKU with no
// table entry, all return null. `globalPrice` is the already-resolved global
// per-stem price for this variety+length+color - used ONLY for the event
// rule (global x 1.10); the wholesaler tier ignores it and reads the table.
export function tierPricePerStem(
  variety: TierClassifiable,
  lengthCm: number | null | undefined,
  tier: PricingTier | null | undefined,
  globalPrice: number | null | undefined,
): number | null {
  if (!tier || tier === "flower_shop") return null;
  if (tier === "event") {
    if (globalPrice == null || !(globalPrice > 0)) return null;
    const raw = round2(globalPrice * EVENT_MULTIPLIER);
    return clampToFloor(variety, lengthCm, "event", raw);
  }
  // wholesaler
  const entry = wholesaleEntry(variety, lengthCm);
  if (!entry) return null;
  return clampToFloor(variety, lengthCm, "wholesaler", entry.wholesale);
}
